/**
 * Content Manager - UI Store
 *
 * Zustand store for managing UI state including views, selections, and modals.
 */

import { create } from 'zustand';
import type { ViewMode, SortConfig, ModalState, Toast, SelectionState } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface UIState {
  // View state
  activeView: ViewMode;
  previousView: ViewMode | null;

  // Expansion state
  expandedSeeds: string[];
  expandedGroups: string[];

  // Selection state
  selectedItems: SelectionState;

  // Sort settings per view
  sortSettings: Record<ViewMode, SortConfig>;

  // Modal state
  modal: ModalState;

  // Toast notifications
  toasts: Toast[];

  // Command palette
  isCommandPaletteOpen: boolean;

  // Help/shortcuts modal
  isHelpOpen: boolean;

  // Config modal
  isConfigOpen: boolean;

  // Sidebar state
  isSidebarCollapsed: boolean;

  // Search
  searchQuery: string;
  isSearchFocused: boolean;

  // Actions - Views
  setActiveView: (view: ViewMode) => void;
  goBack: () => void;

  // Actions - Expansion
  toggleSeedExpanded: (seedId: string) => void;
  expandAllSeeds: () => void;
  collapseAllSeeds: () => void;
  toggleGroupExpanded: (groupId: string) => void;

  // Actions - Selection
  selectSeed: (seedId: string, multi?: boolean) => void;
  selectOutput: (outputId: string, multi?: boolean) => void;
  clearSelection: () => void;
  selectAll: (seedIds: string[], outputIds: string[]) => void;

  // Actions - Sort
  setSortSettings: (view: ViewMode, sort: SortConfig) => void;
  toggleSortDirection: (view: ViewMode) => void;

  // Actions - Modal
  openModal: (modal: Omit<ModalState, 'isOpen'>) => void;
  closeModal: () => void;

  // Actions - Toasts
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Actions - Command palette
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;

  // Actions - Help
  openHelp: () => void;
  closeHelp: () => void;
  toggleHelp: () => void;

  // Actions - Config
  openConfig: () => void;
  closeConfig: () => void;
  toggleConfig: () => void;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Actions - Search
  setSearchQuery: (query: string) => void;
  setSearchFocused: (focused: boolean) => void;
  clearSearch: () => void;
}

// =============================================================================
// DEFAULT SORT SETTINGS
// =============================================================================

const defaultSortSettings: Record<ViewMode, SortConfig> = {
  content: { field: 'order', direction: 'asc' },
  calendar: { field: 'publishDate', direction: 'asc' },
  overview: { field: 'updatedAt', direction: 'desc' },
  silos: { field: 'silo', direction: 'asc' },
  users: { field: 'name', direction: 'asc' },
  audit: { field: 'createdAt', direction: 'desc' },
  performance: { field: 'views', direction: 'desc' },
  events: { field: 'startDate', direction: 'asc' },
};

// =============================================================================
// STORE
// =============================================================================

export const useUIStore = create<UIState>()((set, get) => ({
  // Initial state
  activeView: 'content',
  previousView: null,
  expandedSeeds: [],
  expandedGroups: [],
  selectedItems: { seeds: [], outputs: [] },
  sortSettings: defaultSortSettings,
  modal: { isOpen: false, mode: 'view', type: 'seed' },
  toasts: [],
  isCommandPaletteOpen: false,
  isHelpOpen: false,
  isConfigOpen: false,
  isSidebarCollapsed: false,
  searchQuery: '',
  isSearchFocused: false,

  // ==========================================================================
  // VIEW ACTIONS
  // ==========================================================================

  setActiveView: (view) => {
    set((state) => ({
      activeView: view,
      previousView: state.activeView,
    }));
  },

  goBack: () => {
    const { previousView } = get();
    if (previousView) {
      set((state) => ({
        activeView: previousView,
        previousView: state.activeView,
      }));
    }
  },

  // ==========================================================================
  // EXPANSION ACTIONS
  // ==========================================================================

  toggleSeedExpanded: (seedId) => {
    set((state) => ({
      expandedSeeds: state.expandedSeeds.includes(seedId)
        ? state.expandedSeeds.filter((id) => id !== seedId)
        : [...state.expandedSeeds, seedId],
    }));
  },

  expandAllSeeds: () => {
    // Note: This should be called with all seed IDs from the content store
    // For now, it just expands whatever is currently known
  },

  collapseAllSeeds: () => {
    set({ expandedSeeds: [] });
  },

  toggleGroupExpanded: (groupId) => {
    set((state) => ({
      expandedGroups: state.expandedGroups.includes(groupId)
        ? state.expandedGroups.filter((id) => id !== groupId)
        : [...state.expandedGroups, groupId],
    }));
  },

  // ==========================================================================
  // SELECTION ACTIONS
  // ==========================================================================

  selectSeed: (seedId, multi = false) => {
    set((state) => {
      if (multi) {
        const isSelected = state.selectedItems.seeds.includes(seedId);
        return {
          selectedItems: {
            ...state.selectedItems,
            seeds: isSelected
              ? state.selectedItems.seeds.filter((id) => id !== seedId)
              : [...state.selectedItems.seeds, seedId],
          },
        };
      }
      return {
        selectedItems: {
          seeds: [seedId],
          outputs: [],
        },
      };
    });
  },

  selectOutput: (outputId, multi = false) => {
    set((state) => {
      if (multi) {
        const isSelected = state.selectedItems.outputs.includes(outputId);
        return {
          selectedItems: {
            ...state.selectedItems,
            outputs: isSelected
              ? state.selectedItems.outputs.filter((id) => id !== outputId)
              : [...state.selectedItems.outputs, outputId],
          },
        };
      }
      return {
        selectedItems: {
          seeds: [],
          outputs: [outputId],
        },
      };
    });
  },

  clearSelection: () => {
    set({ selectedItems: { seeds: [], outputs: [] } });
  },

  selectAll: (seedIds, outputIds) => {
    set({ selectedItems: { seeds: seedIds, outputs: outputIds } });
  },

  // ==========================================================================
  // SORT ACTIONS
  // ==========================================================================

  setSortSettings: (view, sort) => {
    set((state) => ({
      sortSettings: {
        ...state.sortSettings,
        [view]: sort,
      },
    }));
  },

  toggleSortDirection: (view) => {
    set((state) => ({
      sortSettings: {
        ...state.sortSettings,
        [view]: {
          ...state.sortSettings[view],
          direction: state.sortSettings[view].direction === 'asc' ? 'desc' : 'asc',
        },
      },
    }));
  },

  // ==========================================================================
  // MODAL ACTIONS
  // ==========================================================================

  openModal: (modal) => {
    set({ modal: { ...modal, isOpen: true } });
  },

  closeModal: () => {
    set((state) => ({
      modal: { ...state.modal, isOpen: false },
    }));
  },

  // ==========================================================================
  // TOAST ACTIONS
  // ==========================================================================

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { ...toast, id };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    const duration = toast.duration || 5000;
    setTimeout(() => {
      get().removeToast(id);
    }, duration);
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },

  // ==========================================================================
  // COMMAND PALETTE ACTIONS
  // ==========================================================================

  openCommandPalette: () => set({ isCommandPaletteOpen: true }),

  closeCommandPalette: () => set({ isCommandPaletteOpen: false }),

  toggleCommandPalette: () =>
    set((state) => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen })),

  // ==========================================================================
  // HELP ACTIONS
  // ==========================================================================

  openHelp: () => set({ isHelpOpen: true }),

  closeHelp: () => set({ isHelpOpen: false }),

  toggleHelp: () => set((state) => ({ isHelpOpen: !state.isHelpOpen })),

  // ==========================================================================
  // CONFIG ACTIONS
  // ==========================================================================

  openConfig: () => set({ isConfigOpen: true }),

  closeConfig: () => set({ isConfigOpen: false }),

  toggleConfig: () => set((state) => ({ isConfigOpen: !state.isConfigOpen })),

  // ==========================================================================
  // SIDEBAR ACTIONS
  // ==========================================================================

  toggleSidebar: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

  setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

  // ==========================================================================
  // SEARCH ACTIONS
  // ==========================================================================

  setSearchQuery: (query) => set({ searchQuery: query }),

  setSearchFocused: (focused) => set({ isSearchFocused: focused }),

  clearSearch: () => set({ searchQuery: '', isSearchFocused: false }),
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectActiveView = (state: UIState) => state.activeView;
export const selectExpandedSeeds = (state: UIState) => state.expandedSeeds;
export const selectExpandedGroups = (state: UIState) => state.expandedGroups;
export const selectSelectedItems = (state: UIState) => state.selectedItems;
export const selectModal = (state: UIState) => state.modal;
export const selectToasts = (state: UIState) => state.toasts;
export const selectIsCommandPaletteOpen = (state: UIState) => state.isCommandPaletteOpen;
export const selectIsHelpOpen = (state: UIState) => state.isHelpOpen;
export const selectIsConfigOpen = (state: UIState) => state.isConfigOpen;
export const selectIsSidebarCollapsed = (state: UIState) => state.isSidebarCollapsed;
export const selectSearchQuery = (state: UIState) => state.searchQuery;

// Check if seed is expanded
export const selectIsSeedExpanded = (seedId: string) => (state: UIState) =>
  state.expandedSeeds.includes(seedId);

// Check if item is selected
export const selectIsSeedSelected = (seedId: string) => (state: UIState) =>
  state.selectedItems.seeds.includes(seedId);

export const selectIsOutputSelected = (outputId: string) => (state: UIState) =>
  state.selectedItems.outputs.includes(outputId);

// Get selection count
export const selectSelectionCount = (state: UIState) =>
  state.selectedItems.seeds.length + state.selectedItems.outputs.length;

// Get sort settings for current view
export const selectCurrentSortSettings = (state: UIState) =>
  state.sortSettings[state.activeView];

export default useUIStore;
